import type { FC } from "react";
import React, { useState } from "react";

import type { GetSettingsQuery as GetSettingsQuerySdk } from "~/core/sdk";
import { changeLanguage } from "~/i18n";
import { ArrowDown, Globe } from "~/icons";
import { Dropdown, Flex, Image, Typography } from "~/shared/ui-kit";
import { useDirectus } from "~/shared/utils/directus/useDirectus";

export type LocaleSelectProps = {
  languages: GetSettingsQuerySdk["languages"];
  availableLanguages: Array<string>;
  language: string;
  variant?: "static" | "positioned";
  offset?: number;
};

function isLanguageCode(
  code: string | null | undefined,
  availableLanguages: Array<string>,
): code is LanguageCode {
  if (code) {
    return availableLanguages.includes(code);
  }
  return false;
}

export const LocaleSelect: FC<LocaleSelectProps> = ({
  languages,
  language,
  availableLanguages,
  variant = "positioned",
  offset,
}) => {
  const { getDirectusImage } = useDirectus();
  const [isActive, setIsActive] = useState(false);
  const selectedLang = languages?.find((item) => {
    if (item?.code === language) {
      return item;
    }

    return undefined;
  });

  return (
    <Dropdown.Trigger
      onOpenChange={(isOpen) => setIsActive(isOpen)}
      onAction={(key) => {
        const index = Number(key);

        if (
          Number.isInteger(index) &&
          index < languages.length &&
          languages[index].code !== language &&
          isLanguageCode(languages[index].code, availableLanguages)
        ) {
          changeLanguage(languages[index].code);
        }
      }}
      strategy="mixed"
      placement="bottom right"
      offset={offset}
      variant={variant}
      triggerElement={
        <div className="cursor-pointer w-full">
          <Flex
            align="center"
            justify="center"
            className={`py-2 hover:stroke-[#9b5ff5] hover:text-[#9b5ff5] ${isActive && "text-[#9b5ff5] stroke-[#9b5ff5]"}`}
          >
            <Globe />
            <Typography
              size="medium"
              weight="semibold"
              className="pr-2 pl-1 uppercase"
            >
              {selectedLang ? selectedLang?.code : "Pick"}
            </Typography>
            <ArrowDown
              className={`${isActive && "transform rotate-[-90deg]"}`}
            />
          </Flex>
        </div>
      }
      dropdownWrapper={(wrapperProps) => (
        <div className="p-2 md:w-[145px]">{wrapperProps.children}</div>
      )}
    >
      {languages?.map(
        (item, index) =>
          item && (
            <Dropdown.Item key={index} textValue={item?.code}>
              <Flex
                align="center"
                justify="center"
                gap={16}
                key={index}
                className="uppercase py-3 cursor-pointer"
              >
                {item?.icon?.id && (
                  <Image
                    value={getDirectusImage(item.icon.id ?? "", {
                      format: "svg",
                      width: 24,
                    })}
                  />
                )}
                <Typography size="medium" className="uppercase">
                  {item?.code}
                </Typography>
              </Flex>
            </Dropdown.Item>
          ),
      )}
    </Dropdown.Trigger>
  );
};
